// English. Same keys as fr.js (the reference), plus `srv` / `srvExact` for the backend's French
// sentences (same keys as tn.js). The game's own words are kept: Nemna3 (first pose), Nefrech
// (going out). Pioche = draw pile, défausse / fausse = discard pile, brelan = set, suite = run,
// manche = round, partie = game, seuil = threshold.
const s = (n) => (n === 1 ? "" : "s");
// A run of cards (A-2-3) never splits over two lines: a word joiner after each hyphen.
const run = (cards) => cards.replace(/-/g, "-⁠");
const cards = (n) => `${n} card${s(n)}`;

const en = {
  meta: { title: "Rami Online — Rummy with friends" },

  lang: {
    label: "Language",
    switched: "The game is now in English 🇬🇧",
  },

  common: {
    brand: "Rami Online",
    lobby: "Lobby",
    error: "Error",
    loading: "Loading…",
    cancel: "Cancel",
    or: "or",
    back: "Back",
    home: "Home",
    you: "You",
    me: "Me",
    yes: "Yes",
    no: "No",
    left: "Left",
    right: "Right",
    pts: "{n} pts",
    playersCount: "{a}/{b} players",
    networkRetry: "Network error, try again",
    connecting: "Connecting…",
    // Google sign-in in the apps (lib/googleLogin.js).
    googleFailed: "Signing in with Google did not work. Try again.",
    piocheSide: "Draw pile position",
    google: "Continue with Google",
    namePlaceholder: "Enter your first name…",
    enterName: "Enter your first name",
    joinArrow: "Join →",
    quit: "Leave",
    join: "Join",
    inProgress: "In progress",
    eliminated: "Eliminated",
    threshold: "Threshold",
    elim: "Elim.",
    jokers: "Jokers",
    rematch: "Play again",
    rematchBadge: "Rematch",
    rematchWaiting: ({ n }) =>
      n > 1 ? "{names} are waiting to play again" : "{names} is waiting to play again",
  },

  landing: {
    titlePre: "",
    titleMain: "Rami Online",
    subtitle: "with friends",
    guestTitle: "Play as a guest",
    googleKeep: "Your profile on all your devices: rank, stats, tables",
    guestKept: "Profile kept on this device only",
    resumeAs: "Continue as",
    newGuest: "New guest profile",
    replaceWarn: "{name}'s profile will be deleted, rank included.",
    guestGone: "This guest profile no longer exists: enter a name to make a new one.",
    guestSub: "Join or create a game",
    nameEnough: "A first name is all you need to join",
    continueArrow: "Continue →",
    piocheHint: "Choose which side of the screen the draw pile sits on during the game.",
    connecting: "Connecting…",
    feat: {
      players: "2 to 4 players",
      bots: "Built-in AI bots",
      realtime: "Real time",
    },
  },

  lobby: {
    hub: {
      ongoing: "Game in progress",
      onlineTag: "4 players",
      onlineSub: "Find 3 opponents in seconds. It counts for your rank.",
      openTables: ({ n }) => (n === 0 ? "No open table" : n === 1 ? "1 open table" : `${n} open tables`),
      friendsTitle: "With friends",
      friendsSub: "Your table, your rules, a code to share",
      create: "Create a table",
      yourTables: "Your tables",
      trainingTitle: "Training",
      trainingSub: "No effect on your rank",
      solo2: "1 vs 1",
      solo4: "4 players",
      level: "Bot level",
      learnTitle: "Learn to play",
      learnSub: "The rules in 9 steps, then a practice game",
      tablePreview: "Your table, as you'll see it in a game.",
    },
    code: {
      button: "Join with a code",
      placeholder: "Code or invite link",
      notFound: "Game not found — check the code or the link",
      inProgress: "game in progress",
    },
    solo1v1: "Solo 1v1",
    solo4: "Solo 4P",
    quitFirst: "Leave your current game first",
    create: "Create a game",
    createShort: "Create",
    newGame: "New game",
    players: "Players",
    nPlayers: "{n} players",
    botsLabel: "Bots ({n} / {max} max)",
    nBots: ({ n }) => (n === 0 ? "No bots" : `${n} bot${s(n)}`),
    botLevel: "Bot level",
    level: { debutant: "Beginner 🐣", medium: "Intermediate 😏", legende: "Legend 🔥" },
    nJokers: "{n} Jokers",
    thresholdLabel: "Starting threshold",
    thresholdTip:
      "Minimum score needed to lay down your combinations for the first time. Below it, you can't open.",
    elimLabel: "Elim. score",
    elimTip:
      "A player is out for good once their total score goes past this value. The last one left wins; at 4 players, the last two win, ranked by score.",
    rulesTitle: "Penalties & rules",
    pureSeq: "Pure run without a Joker required",
    pureSeqTip:
      "When on, the combinations laid down must include at least one run of the same suit without a Joker.",
    pen: {
      standard: {
        label: "Standard penalty",
        tip: "Points added to the score of a player who makes a mistake during play — for example, trying to lay down cards thinking they reach the threshold, then cancelling.",
      },
      joker: {
        label: "Joker in hand",
        tip: "Penalty when a player has laid down some cards but didn't win the round and still holds Jokers.",
      },
      useful: {
        label: "Useful discard",
        tip: "Penalty when a player discards a card that could have gone on a combination already on the table.",
      },
      continuer: {
        label: "“Continue” penalty",
        tip: "Penalty for the player who chose to continue the round and neither laid down any cards nor won the round.",
      },
      nefrechFausse: {
        label: "Round won on an opponent's discard",
        tip: "Penalty when you discard a card that the next player picks up to win the round.",
      },
    },
    createInvite: "Create and invite",
    createCancel: "Cancel",
    playOnline: "Play online",
    play: "Play",
    searching: "Searching…",
    privateTitle: "Private rooms",
    privateDesc: "Create a private game, invite your friends with a link and set the rules the way you like.",
    chip: {
      link: "Invite by link",
      rules: "Custom rules",
      friends: "2 to 4 friends",
      active: "Open while a player is connected",
      botReplace: "Disconnected players replaced by a bot",
      players4: "4 players",
      instant: "Instant access",
      realtime: "Real time",
    },
    resume: "Resume →",
    presetDefault: "Default",
    presetCustom: "Custom",
    viewParams: "View settings",
    copyLink: "Copy link",
    linkCopied: "Link copied!",
    // A room was just created: its code is in the clipboard (Lobby.handleCreate).
    codeCopied: "Code {code} copied: send it to your friends 📲",
    full: "Full",
    noPrivate: "No open private room",
    noPrivateSub: "Create a game and share the link with your friends",
    onlineTitle: "Online games",
    available: "{n} open",
    onlineDesc: "Join a 4-player game in real time. Matchmaking puts you in a room automatically.",
    hostJokers: "{host} · {n} jokers",
    spotsLeft: ({ n }) => `${n} seat${s(n)} left`,
    noOnline: "No room waiting",
    noOnlineSub: 'Tap "Play" to create a new room',
    param: {
      std: "Std penalty",
      jokerHand: "Joker in hand",
      useful: "Useful discard",
      continuer: "Continue",
      nefrechFausse: "Won on a discard",
      pureSeq: "Pure run",
    },
    toast: {
      matchmaking: "Matchmaking error",
      create: "Could not create the game",
      quit: "You left the game",
      solo: "Solo game started against {n} bot(s)",
    },
  },

  tuto: {
    button: "Tutorial",
    title: "Rummy in 1 minute",
    next: "Next",
    skip: "Skip",
    done: "Got it!",
    close: "Close",
    hand: "Your hand",
    who: { leila: "Leila", sami: "Sami" },
    goal: {
      title: "First one out wins",
      body: "Everyone gets 14 cards, and has 15 after drawing. Lay down <b>14 cards</b> in combinations of at least 3, then discard the <b>15th</b> to <b>win the round</b>.",
      tip: "Play with 2 or 4. A game is played over several rounds.",
      win: "Round won!",
    },
    turn: {
      title: "Your turn in 3 steps",
      body: "<b>Tap the draw pile</b> to draw, <b>lay down</b> your combinations if you can, then <b>drag</b> a card onto the discard pile to end your turn.",
      tip: "If you take the last discarded card, you must make your first pose with it: you'll see how in the next steps.",
    },
    combos: {
      title: "Combinations",
      body: "<b>Run</b>: at least 3 consecutive cards of the same suit. <b>Set</b>: 3 or 4 cards of the same rank, in different suits.",
      tip: `The Ace goes before the 2 (${run("A-2-3")}) or after the King (${run("Q-K-A")}), never in the middle (${run("K-A-2")}).`,
      seq: "Run",
      set: "Set",
    },
    joker: {
      title: "The Joker",
      body: "It stands for <b>any card</b>. On the table, you can take it back by laying the real card in its place: it comes back to your hand. In a run, <b>two Jokers never sit side by side</b>.",
      tip: "A Joker still in your hand at the end of the round costs 50 points.",
    },
    save: {
      title: "Save the round, then win it",
      body: "Until you lay something down, the round can cost you <b>100 points</b>. Your first pose <b>saves</b> you: from then on, only the cards in your hand count. Then lay them all down and discard the last one to <b>win the round</b>.",
      tip: "You can also lay everything down in a single turn: that's an instant win.",
      ifEnds: "If the round ends now",
      none: "Nothing down",
      saved: "Round saved",
      won: "Round won",
    },
    nemna3: {
      title: "How to save the round",
      body: "From your 2nd turn, you can lay down. The 1st time, you must reach the <b>threshold</b> (shown at the top), with a <b>run without a Joker</b> if the game's rules ask for one (“Info”). After you, the threshold goes up.",
      tip: `Values: 2 to 9 = their number · 10, J, Q, K = 10 · Ace = 1 in ${run("A-2-3")}, 10 otherwise · Joker = the card it stands for.`,
      pure: "Run without a Joker",
    },
    table: {
      title: "Then: build on the table",
      body: "After your first pose, add your cards to <b>any</b> combination on the table, even other players', and take the discard pile freely.",
      warn: "Discarding a card that could go on the table costs 50 points.",
    },
    score: {
      title: "End of the round",
      body: "The winner scores 0. The others count the cards in their hand, and anyone who laid nothing down takes <b>100</b>. Reach the elimination score and you're out: the last player left wins. At 4 players, the last two win, the lower score first.",
      tip: "Start of a round: if everyone votes “Redeal”, the cards are dealt again. As soon as one player says “Continue”, play starts — and if they lay nothing down, they take 50 more points.",
    },
    ready: {
      title: "Your turn to play!",
      body: "The easiest way to learn: a game against a beginner bot. The Dealer tells you what to do on every turn.",
      bodyGame: "Your game is starting! The <b>Dealer</b>, at the bottom left, tells you what to do on every turn. Tap the Dealer to see the game's history in the <b>log</b>.",
      practice: "Practice against a bot",
    },
  },

  room: {
    addBots: "Add bots",
    role: { bot: "AI bot", host: "Host", player: "Player" },
    waitingSlot: "Waiting…",
    start: "Start",
    startWith: "Start with {n} players",
    waitingCount: "Waiting ({a}/{b})",
    waitingLaunch: "Waiting for the host to start…",
    waitingTitle: "Waiting for players",
    shareHint: "Give your friends the code, or add bots",
    codeLabel: "Game code",
    // The same, in the chip of the top bar (a phone's width).
    codeShort: "Code",
    codeCopied: "Code copied",
    cantStart: "Could not start",
    cantLaunch: "Could not start",
    rematchNote: "Same players, same rules",
    table: {
      title: "Table colour",
      names: {
        "emeraude": "Emerald",
        "bleu-nuit": "Midnight blue",
        "bordeaux": "Burgundy",
        "mediterranee": "Mediterranean",
        "noir-or": "Black & Gold",
        "neon": "Neon",
      },
    },
  },

  // Shared by both waiting rooms (components/room/WaitingTable.jsx and its users).
  waitingTable: {
    full: "Table full!",
    missing: ({ n }) => (n === 1 ? "One more player and we're off!" : `${n} more players and we're off`),
    hostStarts: "Start the game whenever you like",
    waitHost: "The host will start the game",
    noRank: "With bots only, no rank points",
    // Under the count in the middle of the table ("2/4").
    atTable: "seated",
    startsIn: "Starting in",
    ready: "Ready",
    starting: "Starting…",
    tipTitle: "Did you know?",
    // « Le savais-tu ? » (Tips in WaitingTable.jsx), in this order. They follow the room's rules:
    // {n} = the room's penalty; a tip whose rule is off in the room is not shown.
    tips: {
      joker: "The Joker stands for any card. On the table, lay the real card in its place: the Joker comes back to your hand.",
      noPose: "Until you lay something down, the round can cost you 100 points. Your first pose saves you.",
      ace: `The Ace goes before the 2 (${run("A-2-3")}) or after the King (${run("Q-K-A")}), never in the middle.`,
      usefulDiscard: "Discarding a card that could go on the table costs {n} points.",
      jokerHand: "A Joker still in your hand at the end of the round costs {n} points.",
      pureSeq: "Your first pose must include a run without a Joker.",
      threshold: "Every first pose raises the threshold: the next one must score more points.",
      takeDiscard: "Until you lay something down, taking the discard pile means making your first pose with that card.",
      addToTable: "After your first pose, add your cards to any combination on the table, even other players'.",
      continuer: "Start of a round: as soon as one player says “Continue”, play starts. If they lay nothing down, they take {n} more points.",
      botReplace: "If a player drops out, a bot can take their seat: the game goes on.",
      jokersApart: "In a run, two Jokers never sit side by side: 5-6-7 with two Jokers goes down as Joker-5-6-7-Joker.",
    },
  },

  // Waiting room of « Jouer en ligne », with strangers (components/room/OnlineWaitingRoom.jsx).
  onlineRoom: {
    searching: "Finding players",
    startsSoon: "Get comfy, the game is about to start",
    seatSearching: "Searching…",
    elapsed: "Time waited",
    cta: {
      waiting: "Waiting for players ({a}/{b})",
      startNow: "Start now",
      startsIn: ({ n }) => `Starting in ${n} s`,
    },
    bots: {
      ask: "Add bots to start quickly",
      fill: "Fill with bots",
    },
  },

  // Colours of the table locked behind a video (components/TableColorPicker.jsx).
  tableColor: {
    button: "Table",
    title: "Table colour",
    lockedLabel: "{name}: locked, watch a video to unlock it",
    hintApp: "Green is always free. Watch a short video to unlock another colour.",
    hintWeb: "Green is always free. The other colours are unlocked in the Rami Online app, with a short video.",
    appOnly: "This colour is unlocked in the Rami Online app, with a short video.",
    unlocked: "{name} table unlocked",
    dismissed: "Video stopped: watch it to the end to unlock the colour.",
    noVideo: "No video available right now. Try again in a moment.",
    saveError: "The unlock couldn't be saved. Check your connection.",
  },

  join: {
    invited: "You're invited",
    nameEnough: "A first name is all you need to join the game",
    gameOf: "Game of",
    status: { started: "In progress", full: "Full", open: "Waiting" },
    invalidLink: "Invalid link",
    invalidLinkDesc: "This room no longer exists or the link has expired.",
    connectingGame: "Joining the game…",
    fullTitle: "Room full",
    fullDesc: "Every player has joined — this game is about to start.",
    refresh: "Refresh",
    startedTitle: "Game in progress",
    startedDesc: "The game has already started. Create a game to play the next one.",
    cantJoin: "Could not join the room",
    googleSub: "Stats saved between games",
  },

  game: {
    loading: "Loading the game…",
    timer: {
      hurry: "⏳ 10 seconds left!",
      bonus: "+{n}s",
      secs: "{n}s",
    },
    ws: {
      quit: "❌ {name} left the game for good",
    },
    ev: {
      voteCast: "↺ {name} wants a redeal ({a}/{b})",
      voteCastNext: "↺ {name} wants a redeal ({a}/{b}) — {next}'s turn to vote",
      continuer: "▶ {name} chose to continue the round",
      redealDone: "↺ Round redealt! New cards dealt.",
      redealCancelled: "The round goes on — redeal cancelled ({names} had voted)",
    },
    turn: {
      draw: "✋ Your turn — tap the draw pile or take the discard",
      drawOnly: "✋ Your turn — draw (the discard pile isn't available this turn)",
      act: "🃏 Lay down groups or drag a card onto the discard pile",
      discardOnly: "🃏 First turn: no laying down, drag a card onto the discard pile",
    },
    nudge: {
      draw: [
        "⏳ Asleep? 😴 Draw a card!",
        "⏳ Everyone's waiting… your turn to draw!",
        "⏳ The coffee's getting cold while you think ☕",
        "⏳ It's been your turn for a while… play!",
        "⏳ Don't be scared, the card doesn't bite 😄",
        "⏳ Your friends have aged waiting for you 👴",
        "⏳ Hello? Anyone there? It's your turn! 📞",
        "⏳ The draw pile won't run away, but your friends might 😅",
        "⏳ The right card might be waiting in the draw pile 😉",
        "⏳ The tea is cold and the table is getting restless 🍵",
        "⏳ The match is over and you still haven't drawn ⚽",
        "⏳ Even the café waiter is waiting for you 😅",
        "⏳ The turn won't pass by itself… draw!",
        "⏳ What are you thinking about? You haven't even drawn! 😄",
        "⏳ Your neighbour is yawning… go on, draw! 🥱",
        "⏳ One card before dawn would be nice 🌙",
        "⏳ Come on, champ, draw or take the discard!",
        "⏳ Draw pile or discard pile, just pick one! 😄",
        "⏳ The draw pile is winking at you… 😉",
        "⏳ The whole table is holding its breath 😮",
        "⏳ How about a card to start with?",
        "⏳ The Dealer is drumming on the table… your move! 🃏",
        "⏳ Even the Jokers are getting bored 🤡",
        "⏳ We've got time for a second coffee ☕☕",
        "⏳ The game won't play itself without you!",
        "⏳ Your hand is waiting for its 15th card 🖐️",
        "⏳ One card and you're off!",
      ],
      drawOnly: [
        "⏳ Asleep? 😴 Draw a card!",
        "⏳ Everyone's waiting… your turn to draw!",
        "⏳ The coffee's getting cold while you think ☕",
        "⏳ It's been your turn for a while… play!",
        "⏳ Don't be scared, the card doesn't bite 😄",
        "⏳ Your friends have aged waiting for you 👴",
        "⏳ Hello? Anyone there? It's your turn! 📞",
        "⏳ The draw pile won't run away, but your friends might 😅",
        "⏳ The right card might be waiting in the draw pile 😉",
        "⏳ The tea is cold and the table is getting restless 🍵",
        "⏳ The match is over and you still haven't drawn ⚽",
        "⏳ Even the café waiter is waiting for you 😅",
        "⏳ The turn won't pass by itself… draw!",
        "⏳ What are you thinking about? You haven't even drawn! 😄",
        "⏳ Your neighbour is yawning… go on, draw! 🥱",
        "⏳ One card before dawn would be nice 🌙",
        "⏳ The discard pile is closed this turn… the draw pile it is! 😄",
        "⏳ The draw pile is winking at you… 😉",
        "⏳ The whole table is holding its breath 😮",
        "⏳ How about a card to start with?",
        "⏳ The Dealer is drumming on the table… your move! 🃏",
        "⏳ Even the Jokers are getting bored 🤡",
        "⏳ We've got time for a second coffee ☕☕",
        "⏳ The game won't play itself without you!",
        "⏳ Your hand is waiting for its 15th card 🖐️",
        "⏳ One card from the draw pile and you're off!",
        "⏳ This turn it's the draw pile… go for it! 🃏",
      ],
      act: [
        "⏳ Discard a card and set us free! 😅",
        "⏳ We're waiting… laying down or discarding?",
        "⏳ Enough thinking, play now!",
        "⏳ The waiter brought the coffee and you're still thinking ☕",
        "⏳ Go on, discard a card and end your turn",
        "⏳ The cards won't change by themselves 😄",
        "⏳ Your friends are falling asleep… discard! 😴",
        "⏳ Can you lay down? Do it. Otherwise, discard!",
        "⏳ This isn't chess, it's rummy! ♟️",
        "⏳ The night is long, the round isn't… come on!",
        "⏳ That card is burning your hand… discard it! 🔥",
        "⏳ A run in your hand? Lay it down, don't hide it 😏",
        "⏳ The perfect card doesn't exist… discard! 🤷",
        "⏳ Thinking won't bring the Joker 😄",
        "⏳ Time flies and your cards haven't moved ⏰",
        "⏳ The table is ordering more coffee… discard! ☕",
        "⏳ Counted well? Then discard one 😅",
        "⏳ Lay down your sets or discard, the table is waiting 😄",
        "⏳ The whole table is counting your cards with you 🧮",
        "⏳ The Dealer is starting to yawn 🥱",
        "⏳ One decision: which card goes? 🤔",
        "⏳ Your opponents are already planning their revenge 😏",
        "⏳ Cards look better on the table than in your hand 😉",
        "⏳ We saved you a seat… but not for the whole night 🌙",
        "⏳ Even the Joker has made up its mind. And you? 🤡",
        "⏳ Breathe, choose, discard! 😌",
        "⏳ Your neighbour has finished their coffee ☕",
      ],
      discardOnly: [
        "⏳ Discard a card and set us free! 😅",
        "⏳ Enough thinking, play now!",
        "⏳ The waiter brought the coffee and you're still thinking ☕",
        "⏳ Go on, discard a card and end your turn",
        "⏳ The cards won't change by themselves 😄",
        "⏳ Your friends are falling asleep… discard! 😴",
        "⏳ This isn't chess, it's rummy! ♟️",
        "⏳ The night is long, the round isn't… come on!",
        "⏳ That card is burning your hand… discard it! 🔥",
        "⏳ The perfect card doesn't exist… discard! 🤷",
        "⏳ Thinking won't bring the Joker 😄",
        "⏳ Time flies and your cards haven't moved ⏰",
        "⏳ The table is ordering more coffee… discard! ☕",
        "⏳ Counted well? Then discard one 😅",
        "⏳ First turn: only one thing to do, discard! 🃏",
        "⏳ No laying down this turn… pick a card and discard it 😉",
        "⏳ The whole table is counting your cards with you 🧮",
        "⏳ The Dealer is starting to yawn 🥱",
        "⏳ One decision: which card goes? 🤔",
        "⏳ Your opponents are already planning their revenge 😏",
        "⏳ We saved you a seat… but not for the whole night 🌙",
        "⏳ Breathe, choose, discard! 😌",
        "⏳ Your neighbour has finished their coffee ☕",
        "⏳ One card too many in your hand… which one? 🃏",
        "⏳ Discard and the round is off! 🚀",
        "⏳ Pick your least useful card and wave it goodbye! 👋",
      ],
    },
    hint: {
      draw: "Draw a card or take the discard",
      drawOnly: "Draw a card",
      pending: ({ n }) => `${n} combination${s(n)} · drag onto the discard pile`,
      act: "Discard or lay down combinations",
      discardOnly: "First turn: discard a card",
      lastCard: "Discard your last card to win the round",
      belowThreshold: ({ n, missing }) =>
        `${n} combination${s(n)} · ${missing} pt${s(missing)} short of laying down`,
      needPureSeq: ({ n }) => `${n} combination${s(n)} · you need a run without a Joker`,
    },
    err: {
      select3: "Select at least 3 cards",
      invalidCombo: "Invalid combination",
      validation: "Validation error",
      max14Cancelled: "At most 14 cards can be laid down per turn. This group was cancelled ({n} tried).",
      max14: "At most 14 cards can be laid down per turn ({n} tried).",
      keepOne: "Keep at least 1 card in hand to discard.",
      fausseEmpty: "The discard pile is empty",
      notYourTurn: "It's not your turn",
      alreadyDrewTurn: "You've already drawn this turn",
      alreadyDrew: "You've already drawn",
      discardLocked: "The discard pile is locked this turn (previous player eliminated). Draw from the draw pile.",
      discardClosed: "The discard pile isn't available this turn. Draw from the draw pile.",
      oneCardDiscard: "Only one card at a time on the discard pile",
      drawFirst: "You must draw a card before discarding",
      jokerPosUnknown: "Couldn't work out where the Joker goes.",
      jokerNoSlot: "Can't add a Joker to this run: no legal position.",
      jokerNoSlotBothEnds: "Can't add a Joker to this run: no legal position (neither before nor after).",
      jokersSideBySide: "Two Jokers can't sit side by side in a run.",
      jokerCantAdd: "Can't add this Joker to this run.",
      invalidWithCards: "Invalid combination with these cards",
      cantAddNow: "You can't add right now",
      groupFull: "This group is full (4 cards) — nothing more can be added",
      poseFirstForJoker: "Lay down groups first (≥ {n} pts) to take back this Joker",
      pureSeqForJoker: "Your groups need a run without a Joker to take back this Joker",
      replaceImpossible2: "These 2 cards can't replace it",
      jokerInPendingGroup: "This Joker is in a group being composed: remove that group before cancelling.",
      jokerInvalidSeq: "Invalid Joker in this run",
      additionInvalid: "Invalid addition",
      neitherReplaceNorExtend: "This card can neither replace the Joker nor extend the run.",
      cantPoseNow: "You can't lay down right now",
      min3: "At least 3 cards for a group",
    },
    ok: {
      cardTaken: "Card taken",
      stagedTaken: "Card picked up — compose your first pose with this card",
      cancelled: "Action cancelled",
      nemna3CancelledPenalty: "Nemna3 cancelled — +{n} pts penalty",
      jokerUndo: "↩ Joker recovery cancelled",
      modsUndone: "↩ Changes undone",
      jokerLeft: "Joker placed on the left",
      jokerRight: "Joker placed on the right",
      jokerLeftAuto: "Joker placed on the left (auto)",
      jokerRightAuto: "Joker placed on the right (auto)",
      jokerLeftPending: "Joker placed on the left (pending)",
      jokerRightPending: "Joker placed on the right (pending)",
      jokerRecoveredReal: "Joker taken back — real card in place",
      jokerRecovered: "♻️ Joker taken back!",
      addedToGroup: "+{n} card(s) added to the group",
      pendingAdd: "+{n} card(s) waiting to be added",
      cardsAdded: "{n} card(s) added",
      autoNefrech: "🏆 Round won!",
    },
  },

  top: {
    paramsTitle: "Game settings",
    thresholdInit: "Starting threshold",
    elimScore: "Elim. score",
    pureSeqReq: "Pure run required",
    penStd: "Std penalty",
    jokerHand: "Joker in hand",
    usefulDiscard: "Useful discard",
    continuer: "Continue",
    nefrechFausse: "Won on a discard",
    disabled: "Off",
    round: "Round {n}",
    threshold: "Threshold: {n}",
    infos: "Info",
    soundOn: "Turn players' sound on",
    soundOff: "Mute players",
    music: "Music",
    musicOn: "Turn the music on",
    musicOff: "Turn the music off",
    moods: {
      emeraude: "Café jazz",
      "bleu-nuit": "Starry night",
      bordeaux: "Velvet lounge",
      neon: "Synthwave",
      "noir-or": "Noir casino",
      mediterranee: "Andalusian",
    },
  },

  pioche: {
    draw: "Draw",
    discard: "Discard",
    pile: "Draw pile",
    pileCount: "Draw pile ({n})",
    take: "Take",
    closed: "Closed",
    discardLabel: "Discard",
    fausse: "Discard",
    fausseCount: "Discard ({n})",
    taken: "Taken",
    drag: "Drag",
    dropHere: "Drop here",
    empty: "Empty",
    cancel: "Cancel",
  },

  table: {
    set: "Set",
    seq: "Run",
    preparing: "⏳ {name} is composing…",
    cancelAdd: "Cancel addition",
    swap: "♻ Swap",
    addedAndJoker: "Cards added + Joker taken back",
    nAdded: "{n} card(s) added",
    jokerRecovered: "Joker taken back",
    modsPending: "Changes in progress",
    undo: "Undo",
    drop3: "Drag 3+ cards",
  },

  bottom: {
    yourTurn: "Your turn",
    continuer: "Continue",
    relancer: "Restart",
    nCards: "{n} cards",
    sortBy: "Sort",
    sortValue: "Value",
    sortSuit: "Suit",
    emoji: "Send an emoji",
  },

  feed: {
    aria: "Game log",
    dealer: "The Dealer",
    title: "The Dealer · Log",
    empty: "Nothing happened yet…",
  },

  profile: {
    title: "My profile",
    name: "Your name",
    nameHint: "This is the name the others see at the table.",
    avatar: "Your avatar",
    googlePhoto: "Google photo",
    initial: "Initial",
    save: "Save",
    saved: "Profile saved ✓",
    close: "Close",
    google: "Google account",
    guest: "Guest account",
    guestNote: "Your guest profile is kept on this device. Sign in with Google to find it on another one.",
    saveError: "Your profile could not be saved. Try again in a moment.",
    card: "Your player card",
    record: ({ g, w, l }) => `${g} game${g === 1 ? "" : "s"} · ${w} W · ${l} L`,
    account: "Account",
    avatars: {
      wolf: "Wolf", goldlion: "Gold lion", eagle: "Eagle", cobra: "Cobra", scorpion: "Scorpion", skull: "Skull",
      nvg: "Operator", reticle: "Reticle", ace: "Winged ace", king: "King", joker: "Joker", rank: "Rank",
    },
  },

  rank: {
    title: "Rank",
    tabs: { me: "My rank", board: "Leaderboard", recent: "Recent" },
    tiers: { bronze: "Bronze", argent: "Silver", or: "Gold", platine: "Platinum", diamant: "Diamond", maitre: "Master" },
    pr: "{n} RP",
    gain: "+{n} RP",
    drop: "−{n} RP",
    calibrating: "Placement",
    calibrationCount: "{n}/{total} games",
    calibrationNote: "Your first {n} games with other players count double. Your rank shows after them.",
    toNext: "{n} RP to {rank}",
    top: "The highest rank",
    stats: {
      games: "Games",
      wins: "Wins",
      losses: "Losses",
      winRate: "Win rate",
      streak: "Current streak",
      bestStreak: "Best streak",
      roundsWon: "Rounds won",
      abandons: "Games left",
      peak: "Best rank",
    },
    last: "Last game",
    ladder: "The ranks",
    how: {
      title: "How does it work?",
      place: "A game wins or loses you rank points (RP), depending on where you finish at the table.",
      humans: "It counts if at least 2 real players start it, even if you finish with the bots. Against bots alone: no points, no stats.",
      winners: "At 4 players a game has 2 winners, ranked by score: the 1st earns more points than the 2nd.",
      strength: "Beating stronger players pays more, losing to weaker ones costs more. Bots at the table count by their level.",
      kept: "A rank once reached is yours: you never drop below it.",
      quit: "Leaving a game still running counts as a loss, with 10 RP more.",
    },
    guestNote: "Guest profile: your rank is kept on this device. Sign in with Google to have it everywhere.",
    board: {
      you: "You",
      position: "Your place: {n}",
      unranked: "Finish your placement games to join the leaderboard.",
      empty: "Nobody is ranked yet. Your turn!",
      error: "The leaderboard is not available right now.",
    },
    card: { record: "{w} W · {l} L", open: "See my rank" },
    change: {
      win: "Victory",
      winSecond: "Win · 2nd",
      loss: "{place} of {of}",
      abandon: "Left the game",
      promoted: "Promoted!",
      newTier: "New rank!",
      placed: "Rank earned",
      calibration: "Placement {n}/{total}",
    },
  },

  players: {
    title: "Player profile",
    status: { online: "Online", in_game: "In a game", offline: "Offline" },
    guest: "Guest",
    you: "That's you!",
    invite: "Invite to play",
    invited: "Invitation sent ✓",
    waitNote: "Your invitation will wait 10 minutes in their Lobby.",
    finishGame: "Finish your game to invite them.",
    sameTable: "Already at your table.",
    notFound: "This player no longer exists.",
    error: "Something went wrong. Try again in a moment.",
    recentEmpty: "The players of your next games will show here.",
    open: "See {name}'s profile",
  },

  invite: {
    title: "{name} invites you to play",
    sub: "A friends' table is waiting for you",
    expires: "{n} min left",
    join: "Join",
    decline: "Decline",
    quitFirst: "Leave your current game to join.",
    error: "This invitation is no longer valid.",
    more: ({ n }) => (n === 1 ? "+1 more invitation" : `+${n} more invitations`),
    seatPending: "Invited",
    seatDeclined: "Declined",
  },

  nav: { label: "Main menu", play: "Play", rank: "Rank", social: "Social", table: "Table", profile: "Profile" },
  settings: {
    title: "Settings",
    sound: "Sound effects",
    tutorial: "Watch the tutorial again",
    privacy: "Privacy",
    logout: "Sign out",
  },
  social: {
    invites: "Invitations",
    noInvites: "No invitation yet. When a player invites you, it shows here.",
    recent: "Recent players",
    inviteHint: "Invite a player: your table waits 10 minutes in their Lobby.",
    invite: "Invite",
    sent: "Sent",
  },

  account: {
    delete: "Delete my account",
    deleteTitle: "Delete your account?",
    deleteBody: "Your account, your name and your unlocked tables will be erased for good. If you are in a game, you leave it. This cannot be undone.",
    deleteConfirm: "Delete for good",
    deleteError: "The account could not be deleted. Try again in a moment.",
  },

  redeal: {
    title: "Redeal the round?",
    question: "Happy with your cards?",
    lookHand: "Take a good look at your {n} cards first",
    rejouer: "Redeal",
    rejouerHint: "New cards",
    continuer: "Continue",
    continuerHint: "Keep my cards",
    stake: "When you choose Continue and lay nothing down: +{n} pts",
    thinking: "{name} is deciding…",
    youVoted: "You said Redeal",
    turnSoon: "Your turn to vote is coming",
    chose: "{name} chose to continue",
    ok: "OK",
  },

  summary: {
    gameOver: "🏆 Game over",
    roundOver: "Round {n} over",
    gameWin: "{name} wins the game!",
    roundWin: "{name} <b>won the round</b>!",
    nefrechBadge: "Round won ✓",
    notPosed: "Didn't lay down",
    winningCombos: "Winning combinations",
    kind: { sequence: "Run", set: "Set" },
    discarded: "Card discarded",
    posedCombos: "Combinations laid down",
    remaining: "Cards left ({n})",
    handBefore: "Hand before winning ({n})",
    nextRound: "Next round",
    waitHost: "Next round: waiting for {name}",
    host: "the host",
    noWinner: "No winner this round",
    backLobby: "Back to the lobby",
  },

  winner: {
    funny: [
      "You didn't win a game, you gave a lesson. 🃏",
      "Legends aren't made, they're revealed — you just revealed yourself. 👑",
      "That wasn't a game, it was a masterclass. Next session: paid. 💰",
      "You laid your cards down like others hang works of art. Untouchable. 🖼️",
      "In 10 years, people will still talk about this game. 🏛️",
      "The table is yours. The cards knew it from the start. 🎴",
      "As the wise man says: he who wins last wins best. 🎴",

      "They say luck favours the bold. You don't even need luck. 😏",
      "Your opponents played their best. That's the whole problem. 💀",
      "Final score: you 1, everyone else 0. It's maths. 📐",
      "You won so easily we wonder if you were even paying attention. 😴",
      "While they counted their cards, you were counting your winning points. 🧮",
      "Such a clean win that even the referee is clapping. 👏",
      "The cards talk, and tonight they told your story. ✨",

      "Your opponents will analyse their mistakes for hours… and find new excuses. 😅",
      "Official champion! They want a rematch — spoiler: same ending. 😈",
      "Even the bots bow to your Rummy skills. 🤖",
      "The other players look at you differently now… with respect and envy. 😤",
      "Nobody saw you coming… and that's exactly what style is. 😎",
      "They're still looking for the ace up your sleeve. 🃏",
      "We're renaming the game: “[YOUR NAME]'s Rummy”. 📝",
      "They tried their luck. You just played Rummy. 🤷",
      "Well-earned victory, humility optional. 🏆",
      "You discard opponents like a pro of the green felt. 🎰",

      "That's not a win, it's a show of absolute domination. 🔥",
      "You read the game like an open book. The others weren't even on the same chapter. 📖",
      "Blessed hand, blessed brain. Total respect. 🧠",
      "When you play, the others learn. They should thank you. 🎓",
      "The bots ran every probability. None predicted someone this strong. 🤖",
      "Tonight you didn't play cards. You mastered them. 🎴",
    ],
    champion: "CHAMPION!",
    subtitle: "Rami Online champion",
    second: "2ND PLACE!",
    secondSubtitle: "You win this game too",
    secondLine: "Two places on the podium, and one of them is yours. Next time, the top one!",
    ranking: "Final ranking",
    eliminated: "eliminated",
    closing: "Closing…",
    backLobby: "↩ Back to the lobby",
  },

  joker: {
    title: "Joker position",
    left: "← left",
    right: "right →",
  },

  opp: {
    replaceBot: "Replace with a bot",
    bot: "BOT",
    offline: "Offline",
    away: "Away",
    awayTitle: "Let their time run out: short turns until they're back",
    watchingAd: "Colour ad",
    watchingAdTitle: "Watching an ad to unlock a table colour",
    // Tag under the name of the player who chose "Continue" at the redeal vote.
    continuer: "Kept the deal",
    continuerTitle: "Chose to play on instead of a redeal: a penalty if they don't lay down before the round ends",
    cards: ({ n }) => `{n} card${s(n)}`, // {n} stays: tRich puts the bold number there
    fausse: "+Discard",
    elimSuffix: " · Eliminated",
    nefrechInProgress: "⚡ {name} is winning the round…",
    preparing: "🃏 {name} is composing a pose…",
    addsExisting: "+{n} card(s) on existing groups",
  },

  score: { title: "Scores" },

  voice: {
    unsupported: "Microphone not supported in this browser",
    insecure: "Microphone unavailable: insecure connection (HTTPS required)",
    on: "Microphone on",
    denied: "Permission denied. Allow the microphone in the browser settings.",
    notFound: "No microphone found on this device.",
    busy: "Microphone already used by another app.",
    inaccessible: "Microphone unavailable ({reason})",
    unknown: "unknown error",
    off: "Microphone off",
    enable: "Turn the microphone on",
    disable: "Turn the microphone off",
    lost: "Microphone interrupted. Tap the icon to turn it back on.",
    held: "Microphone paused (used by the system)",
    linkConnecting: "Connecting voice…",
    failNetwork: "No voice with {name}: your networks block a direct connection.",
    failSilent: "No voice with {name}: their device doesn't answer. They need to reload the page.",
    failOutdated: "No voice with {name}: their page is out of date. They need to reload it.",
    details: "Details",
    reportTitle: "Voice diagnostics",
    reportHint: "Technical report of the voice link. Copy it and send it to support.",
    refresh: "Refresh",
    copy: "Copy",
    copied: "Report copied",
  },

  notif: { yourTurn: "It's your turn! (Round {n})" },

  // ── Backend messages ─────────────────────────────────────────────────────
  // The server writes its log and errors in French; lib/i18n/server.js matches them and
  // renders these templates instead (same keys as tn.js).
  srv: {
    joker: "Joker",
    listSep: ", ",
    kindSeq: "run",
    kindSet: "set",
    thresholdNote: " — 🆙 threshold: {n} pts",
    nCards: ({ n }) => cards(n),

    roomOf: "{name}'s game",
    roomOnline: "Online game",
    roomSolo: "Solo · {name}",

    reconnected: "🟢 {name} is back.",
    disconnected: "📡 {name} lost the connection.",
    gameStarted: "Game started. {name} begins with 15 cards.",
    roundStarted: "Round {n} — {name} begins.",
    reshuffled: "The draw pile ran out — the discard pile was shuffled back into it.",
    tookDiscard: "{name} took {card} from the discard pile.",
    tookAndPosed: "🃏 {name} took the discard and laid down {combos} ({pts} pts){note}.",
    posed: "🃏 {name} laid down {combos} ({pts} pts){note}.",
    discarded: "{name} discarded a card.",
    setBroken: "📤 {name}'s set is complete: broken, its {n} cards go under the discard pile.",
    tookWithoutPosing: "⚠️ {name} took the discard without laying it down in Nemna3 (+{n} pts penalty). Card returned.",
    usefulDiscard:
      "⚠️ {name} discarded {card} although it could go on {owner}'s {kind}. Card returned, +{n} pts penalty.",
    added: "➕ {name} added {cards} to {target}'s {kind}.",
    jokerRecovered: "♻️ {name} took back a Joker (from {target}'s group) with {card}.",
    jokerRecovered2: "♻️ {name} took back a Joker (from {target}'s group) with {card} + {extra}.",
    nefrech: "🏆 {name} won the round!",
    nefrechJokers: ({ n }) =>
      n === 1
        ? "🏆 {name} won the round with a Joker still in hand!"
        : "🏆 {name} won the round with {n} Jokers still in hand!",
    nefrechFausse: "⚠️ {name} won the round with a card discarded by {victim} → +{n} pts for {victim}.",
    badJokerSwap: "⚠️ {name} tried an invalid Joker swap (+{n} pts penalty).",
    undoJoker: "↩️ {name} put the Joker back ({target}'s group).",
    undoAdd: "↩️ {name} cancelled an addition to {target}'s group.",
    undoAll: "↩️ {name} undid all changes on {target}'s group.",
    eliminatedMidRound: "❌ {name} is eliminated mid-round (score: {score}).",
    returnedCards: ({ n }) => `📥 {name} returned ${cards(n)} to the discard pile.`,
    wonAllOut: "🏆 {name} wins the game: everyone else is out!",
    cancelledNemna3: "{name} cancelled their Nemna3.",
    cancelledNemna3Penalty: "{name} cancelled their Nemna3 (+{n} pts penalty).",
    refusedPoseNoPureRun: "{name} tried a pose that was refused: no run without a Joker (+{n} pts penalty).",
    refusedPoseThreshold:
      "{name} tried a pose that was refused: threshold not reached, {pts} pts of {threshold} (+{n} pts penalty).",
    cancelledNefrech: "{name} cancelled their Nefrech.",
    cancelledNefrechPenalty: "{name} cancelled their Nefrech (+{n} pts).",
    redealt: "Redeal! New cards dealt — {name} begins with 15 cards.",
    choseContinue: "{name} chose to continue — the round goes on.",
    timeUpTurn: "⏱ Time's up: the Dealer plays for {name}.",
    timeUpDraw: "⏱ Time's up: the Dealer draws for {name}.",
    timeUpVote: "⏱ Time's up: {name} passes their vote.",
    wantsRedealAll: "{name} wants a redeal ({a}/{b}) — dealing again.",
    wantsRedealNext: "{name} wants a redeal ({a}/{b}) — {next}'s turn to vote.",
    noPose: "{name} didn't lay down → +{n} pts.",
    continuedNoPose: "{name} chose to continue and didn't lay down → +{n} pts.",
    endsWithCards: ({ n }) => `{name} ends the round with ${cards(n)} in hand → +{pts} pts.`,
    endsWithCardsJokers: ({ n }) =>
      `{name} ends the round with ${cards(n)} in hand (including {jokers} Joker(s)) → +{pts} pts.`,
    eliminated: "{name} is eliminated (score: {score}).",
    wonGame: "🏆 {name} wins the game! 👑",
    wonGameTwo: "🏆 {name} wins the game, {second} comes 2nd! 👑",

    notYourTurnOf: "Not your turn ({name}'s turn)",
    cantDrawNow: "You can't draw right now",
    cantDiscardNow: "You can't discard right now",
    cantPoseNow: "You can't lay down right now",
    fausseNeedsDrawPhase: "You can only take the discard pile's card while drawing",
    fausseMustBeUsedPenalty: "The card taken from the discard pile must be used in your pose. (+{n} pts penalty)",
    thresholdMissing:
      "Nemna3 threshold not reached: {pts} pts laid down, {missing} short (threshold: {threshold} pts). Add cards or cancel before discarding.",
    poseTooLow: "Threshold not reached: you have {pts} pts, the threshold is {threshold}.",
    cantReplaceInSet: "This card can't replace the Joker in this set: {err}",
    invalidAfterSwap: "The group isn't valid after the swap: {err}",
    invalidAfterSwapPenalty: "The group isn't valid after the swap: {err} (+{n} pts penalty)",
    cantAddToSet: "Can't add to this set: {err}",
    max14Group:
      "At most 14 cards can be laid down per turn, including cards added to existing groups ({n} tried). The last group was cancelled.",
    max14Add:
      "At most 14 cards can be laid down per turn, including cards added to existing groups ({n} tried). The last addition was cancelled.",
    max14Swap: "At most 14 cards can be laid down per turn ({n} tried, Joker swaps included). The swap was cancelled.",
    usefulDiscardErr: "{card} could go on {owner}'s {kind}. Card returned, +{n} pts penalty.",
    cardFitsCombo: "This card can be added to a {kind} already on the table",
    jokerStandsFor: "This Joker stands for a {rank} in the run — only that card can replace it.",
    cancelPenalty: "Cancelled: +{n} pts",
    notYourVote: "Not your turn to vote ({name}'s turn)",
    voteSaved: "Vote saved — {name}'s turn",
    needExactPlayers: "Exactly {n} players are needed to start",
    botsRange: "The number of bots must be between 0 and {n}",
    penCard: "{card}",
  },

  // Fixed backend sentences, keyed by the exact French text.
  srvExact: {
    "Ce n'est pas votre tour": "It's not your turn",
    "Joueur éliminé": "You're eliminated",
    "Pioche vide, manche terminée": "Draw pile empty, round over",
    "Carte piochée": "Card drawn",
    "Défausse vide": "Discard pile empty",
    "La Fausse est vide": "The discard pile is empty",
    "Premier tour : vous devez piocher depuis la pioche, pas depuis la défausse.":
      "First turn: you must draw from the draw pile, not the discard pile.",
    "La défausse est verrouillée ce tour-ci (cartes héritées d'un joueur éliminé). Vous devez piocher depuis la pioche.":
      "The discard pile is locked this turn (cards from an eliminated player). Draw from the draw pile.",
    "La première défausse d'un joueur ne peut pas être récupérée par le joueur suivant. Piochez depuis la pioche.":
      "A player's first discard can't be taken by the next player. Draw from the draw pile.",
    "Carte prise de la défausse": "Card taken from the discard pile",
    "Carte prise + pose effectuée": "Card taken and laid down 👌",
    "Impossible d'agir pendant le vote de redonne": "You can't play during the redeal vote",
    "La carte prise de la Fausse doit être utilisée dans un de vos groupes ou ajouts.":
      "The card taken from the discard pile must be used in one of your groups or additions.",
    "Vous devez d'abord piocher une carte avant de défausser (cycle 14 → pioche → 15 → défausse → 14).":
      "Draw a card before discarding (14 → draw → 15 → discard → 14).",
    "Carte introuvable dans votre main": "This card isn't in your hand",
    "Un Joker ne peut être défaussé que si toutes les cartes restantes sont aussi des Jokers.":
      "A Joker can only be discarded when every card left is a Joker too.",
    "Carte défaussée": "Card discarded",

    "Chaque groupe doit contenir au moins 3 cartes": "Each group needs at least 3 cards",
    "Une carte est utilisée dans plusieurs groupes": "A card is used in several groups",
    "Une carte dans plusieurs groupes": "A card is used in several groups",
    "Carte absente de la main": "This card isn't in your hand",
    "Carte absente de votre main": "This card isn't in your hand",
    "Carte absente": "This card isn't in your hand",
    "La carte à défausser ne peut pas être dans un groupe": "The card to discard can't be in a group",
    "Vous ne pouvez pas poser avant votre 2ème tour": "You can't lay down before your 2nd turn",
    "Pose invalide: au moins une suite pure sans Joker requise":
      "Invalid pose: at least one pure run without a Joker is required",
    "Vous devez inclure au moins une suite pure de même couleur sans Joker":
      "You must include at least one pure run of the same suit without a Joker",
    "Il faut au moins une suite pure sans Joker": "At least one pure run without a Joker is needed",
    "Aucune combinaison fournie": "No combination given",
    "La carte de la défausse doit être utilisée dans Nemna3": "The discard pile's card must be used in your Nemna3",
    "Vous devez garder au moins une carte en main pour défausser. C'est en la défaussant que vous gagnez la manche.":
      "Keep at least one card to discard: discarding it is how you win the round.",
    "Vous devez garder au moins une carte pour défausser. C'est en la défaussant que vous gagnez la manche.":
      "Keep at least one card to discard: discarding it is how you win the round.",

    "Ajout vide": "Nothing to add",
    "Joueur cible introuvable": "Player not found",
    "Joueur cible introuvable (remplacement)": "Player not found",
    "Joueur cible introuvable pour ajout": "Player not found",
    "Groupe cible introuvable": "Group not found",
    "Groupe cible introuvable (remplacement)": "Group not found",
    "Ce groupe est complet (4 cartes) — aucun ajout possible": "This group is full (4 cards) — nothing more can be added",
    "Ce groupe est complet (4 cartes)": "This group is full (4 cards)",
    "Cette carte est déjà utilisée ou est la carte à défausser": "This card is already used or is the card to discard",
    "Ce brelan est verrouillé (complété sans Joker)": "This set is locked (completed without a Joker)",
    "Ce brelan est verrouillé (complété sans Joker) — aucun ajout possible":
      "This set is locked (completed without a Joker) — nothing can be added",
    "Ce brelan est verrouillé — aucun ajout possible": "This set is locked — nothing can be added",
    "Une carte est utilisée plusieurs fois": "A card is used more than once",
    "Une carte est utilisée plusieurs fois (remplacement)": "A card is used more than once",
    "Une carte utilisée plusieurs fois": "A card is used more than once",
    "Carte d'ajout absente de la main": "The card to add isn't in your hand",
    "La carte à défausser ne peut pas être ajoutée à un groupe": "The card to discard can't be added to a group",
    "Ajout invalide à un groupe existant (suite invalide ou cette carte déplacerait un Joker déjà posé).":
      "Invalid addition (the run would break, or this card would move a Joker already on the table).",
    "Ajout invalide (suite invalide ou cette carte déplacerait un Joker déjà posé).":
      "Invalid addition (the run would break, or this card would move a Joker already on the table).",
    "Impossible d'ajouter maintenant (pas en phase d'action)": "You can't add right now",
    "Vous devez d'abord poser des combinaisons valides avant d'ajouter à un groupe":
      "Lay down valid combinations first, then add to groups",
    "Impossible d'ajouter: la suite ne reste pas valide (ou cette carte déplacerait un Joker déjà posé).":
      "Can't add: the run would break (or this card would move a Joker already on the table).",
    "La suite ne reste pas valide après ajout (ou cette carte déplacerait un Joker déjà posé).":
      "The run would break with this addition (or this card would move a Joker already on the table).",
    "Cette carte n'a pas été ajoutée à ce groupe ce tour": "This card wasn't added to this group this turn",
    "Carte introuvable dans le groupe": "Card not found in the group",
    "Ajout annulé": "Addition cancelled",
    "Aucune modification à annuler sur ce groupe": "Nothing to undo on this group",
    "Impossible d'annuler : une carte retirée du groupe n'est plus en main (a-t-elle été posée ailleurs ?).":
      "Can't undo: a card taken from the group is no longer in your hand (did you lay it down elsewhere?).",
    "Modifications annulées": "Changes undone",

    "Pour récupérer le Joker de ce brelan, posez une seule carte réelle":
      "To take back this set's Joker, lay down a single real card",
    "Carte de remplacement absente de la main": "The replacement card isn't in your hand",
    "Carte de remplacement introuvable dans votre main": "The replacement card isn't in your hand",
    "Carte de remplacement introuvable dans le groupe": "The replacement card isn't in the group",
    "Un Joker ne peut pas remplacer un autre Joker dans ce brelan": "A Joker can't replace another Joker in this set",
    "On ne peut pas remplacer un Joker par un autre Joker": "A Joker can't replace another Joker",
    "Vous ne pouvez pas remplacer un Joker par un autre Joker": "A Joker can't replace another Joker",
    "Pour récupérer un Joker, vous devez d'abord poser des combinaisons valides (seuil Nemna3 atteint).":
      "To take back a Joker, lay down valid combinations first (Nemna3 threshold reached).",
    "Vous devez d'abord poser des combinaisons valides avant de récupérer un Joker":
      "Lay down valid combinations first, then take back the Joker",
    "Un Joker récupéré ce tour attend votre pose : posez vos groupes, ou annulez.":
      "A Joker taken back this turn is waiting for your pose: lay down your groups, or cancel.",
    "Joker introuvable dans ce groupe": "No Joker in this group",
    "Joker introuvable dans ce groupe (remplacement)": "No Joker in this group",
    "Joker introuvable dans la main (récupération déjà annulée ?)":
      "The Joker isn't in your hand (recovery already cancelled?)",
    "La carte à défausser ne peut pas servir de remplacement de Joker":
      "The card to discard can't be used to replace a Joker",
    "Groupe de 3 cartes : pour récupérer le Joker, fournissez 2 cartes (remplacement + 4e d'un type différent).":
      "3-card group: to take back the Joker, give 2 cards (the replacement + a 4th of another suit).",
    "Ce groupe ne contient que 3 cartes : pour récupérer le Joker, vous devez fournir 2 cartes (la carte manquante + une 4e d'un type différent).":
      "This group has only 3 cards: to take back the Joker, give 2 cards (the missing card + a 4th of another suit).",
    "Ce groupe ne contient que 3 cartes : 2 cartes nécessaires.": "This group has only 3 cards: 2 cards needed.",
    "La carte à défausser ne peut pas servir de carte additionnelle": "The card to discard can't be the extra card",
    "Carte additionnelle absente de la main": "The extra card isn't in your hand",
    "Carte additionnelle introuvable dans votre main": "The extra card isn't in your hand",
    "La carte additionnelle ne peut pas être un Joker": "The extra card can't be a Joker",
    "La carte additionnelle doit être différente de la carte de remplacement":
      "The extra card must be different from the replacement card",
    "La carte de remplacement ne peut pas être dans les groupes posés":
      "The replacement card can't be in the groups you lay down",
    "La carte additionnelle ne peut pas être dans les groupes posés":
      "The extra card can't be in the groups you lay down",
    "Placement Joker impossible à gauche : aucune carte n'existe avant ce rang dans la suite.":
      "The Joker can't go on the left: no card comes before in this run.",
    "Placement Joker impossible à droite : aucune carte n'existe après ce rang dans la suite.":
      "The Joker can't go on the right: no card comes after in this run.",
    "Position invalide pour ce Joker : la suite ne reste pas valide. Essayez l'autre côté.":
      "Invalid position for this Joker: the run would break. Try the other side.",
    "Joker récupéré depuis le brelan": "Joker taken back from the set 😎",
    "Joker récupéré": "Joker taken back 😎",
    "Récupération annulée": "Joker put back",
    "Aucun Joker remplaçable par cette carte": "This card can't replace any Joker",

    "Impossible de gagner la manche maintenant": "You can't win the round right now",
    "Main insuffisante pour gagner la manche": "Not enough cards to win the round",
    "Carte à défausser introuvable": "Card to discard not found",
    "Toutes les cartes (sauf la défausse) doivent être placées (groupe ou ajout)":
      "Every card (except the discard) must be placed (group or addition)",
    "Manche gagnée": "Round won! 🔥",
    "Manche gagnée — Victoire !": "You win the round! 🏆",
    "Pas en phase de vote de redonne": "Not during the redeal vote",
    "Vote déjà résolu": "The vote is already settled",
    "Joueur introuvable ou éliminé": "Player not found or eliminated",
    "Choix invalide : 'rejouer' ou 'continuer'": "Invalid choice: “Redeal” or “Continue”",
    "La manche précédente n'est pas terminée": "The previous round isn't over",
    "Partie terminée : il ne reste plus que des bots, tous les joueurs réels sont éliminés.":
      "Game over: only bots are left, every real player is eliminated. 🤖",
    "Partie terminée : tous les joueurs sont éliminés.": "Game over: every player is eliminated.",
    "La pioche a été remélangée depuis la défausse.": "The discard pile was shuffled back into the draw pile.",

    "Une combinaison doit contenir au moins 3 cartes": "A combination needs at least 3 cards",
    "Trop de cartes dans la combinaison": "Too many cards in the combination",
    "Un brelan/carré ne peut pas dépasser 4 cartes": "A set can't have more than 4 cards",
    "Le brelan/carré doit contenir au moins 2 vraies cartes": "A set needs at least 2 real cards",
    "Toutes les cartes du brelan doivent avoir la même valeur": "Every card of a set must have the same rank",
    "Impossible d'avoir deux cartes identiques dans un brelan": "A set can't hold two identical cards",
    "Trop de jokers pour un brelan/carré": "Too many Jokers for a set",
    "Deux Jokers ne peuvent pas se suivre dans une suite": "Two Jokers can't sit side by side in a run",
    "La suite doit contenir au moins 2 vraies cartes (max 1 Joker par tranche de 2 cartes)":
      "A run needs at least 2 real cards (at most 1 Joker per 2 cards)",
    "Toutes les cartes d'une suite doivent être de la même couleur": "Every card of a run must be of the same suit",
    "Une suite ne peut pas contenir deux cartes identiques": "A run can't hold two identical cards",
    "Séquence invalide (les cartes doivent être consécutives)": "Invalid run (the cards must be consecutive)",

    "Pénalité défausse utile": "Useful discard penalty",
    "Pénalité standard (Nemna3 annulé ou refusé)": "Standard penalty (Nemna3 cancelled or refused)",
    "Pénalité — défausse utilisée par le gagnant": "Penalty — discard used by the winner",
    "Aucune combinaison posée pendant la manche": "Laid nothing down during the round",
    "A choisi de continuer sans poser de combinaison": "Chose to continue without laying down",
    "Joker": "Joker",

    "Joueur introuvable": "Player not found",
    "Joueur introuvable dans la partie": "Player not in this game",
    "La partie a déjà commencé": "The game has already started",
    "Partie déjà démarrée": "The game has already started",
    "Partie non démarrée": "The game hasn't started",
    "Seul l'hôte peut configurer les bots": "Only the host can set the bots",
    "Impossible : il y a déjà trop de joueurs humains pour ce nombre de bots":
      "Not possible: too many human players for this number of bots",
    "Un bot ne peut pas quitter": "A bot can't leave",
    "Joueur déjà sorti de la partie": "Player already left the game",
    "Partie quittée": "You left the game",
    "Un bot ne peut pas remplacer un joueur": "A bot can't replace a player",
    "Seul l'hôte peut remplacer un joueur par un bot": "Only the host can replace a player with a bot",
    "L'hôte ne peut pas se remplacer lui-même": "The host can't replace themselves",
    "Ce slot est déjà un bot": "This seat is already a bot",
    "Un joueur éliminé n'a pas besoin d'être remplacé par un bot": "An eliminated player doesn't need a bot",
    "Ce joueur est toujours connecté": "This player is still connected",
    "Ce joueur regarde une pub, il revient": "This player is watching an ad and will be right back",
    "Joueur remplacé par un bot": "Player replaced by a bot",
    "Room introuvable": "Game not found",
    "Salle introuvable": "Room not found",
    "Salle introuvable ou lien expiré": "Room not found or link expired",
    "Room pleine": "Room full",
    "Cette salle est réservée aux joueurs de la partie précédente":
      "This room is reserved for the players of the previous game",
    "La partie n'est pas terminée": "The game isn't over",
    "Couleur de table inconnue": "Unknown table colour",
    "Impossible de quitter une partie en cours": "You can't leave a game in progress",
    "Seul l'hôte peut démarrer": "Only the host can start",
    "Il faut au moins 2 joueurs pour démarrer": "At least 2 players are needed to start",
    "Seul l'hôte peut lancer la partie": "Only the host can start the game",
    "Seul l'hôte peut relancer la manche": "Only the host can start the next round",
    "Erreur de matchmaking": "Matchmaking error",
    "Cette partie n'existe plus": "This game no longer exists",
    "Vous avez été remplacé par un bot": "You were replaced by a bot",
    "Vous avez quitté cette partie": "You left this game",
    // Invitations (backend/social.py, server.py)
    "Tu ne peux pas t'inviter toi-même": "You can't invite yourself",
    "Ce joueur a refusé ton invitation, réessaie plus tard": "This player declined your invitation, try again later",
    "Trop d'invitations envoyées, réessaie dans un moment": "Too many invitations sent, try again in a moment",
    "Cette invitation n'est plus valable": "This invitation is no longer valid",
    "Termine ta partie avant d'inviter": "Finish your game before inviting",
    "Ce joueur est déjà à ta table": "This player is already at your table",
    "Ta table est complète": "Your table is full",
    "On n'invite pas dans une partie en ligne": "You can't invite to an online game",
    "Termine ta partie avant d'en rejoindre une autre": "Finish your game before joining another one",

    "Non authentifié": "Please sign in",
    "Session invalide": "Session expired, sign in again",
    "Session expirée": "Session expired, sign in again",
    "Utilisateur introuvable": "Account not found",
    "Le nom est requis": "Your name is required",
    // The guest kept on its device (backend/auth.py)
    "Profil invité introuvable": "This guest profile no longer exists",
    "Clé d'appareil invalide": "This device could not be recognised",
    "Réservé aux profils invités": "For guest profiles only",
    // Profile (backend/player_profile.py)
    "Le nom doit faire au moins 2 caractères": "Your name needs at least 2 characters",
    "Le nom ne peut pas dépasser 20 caractères": "Your name can't be longer than 20 characters",
    "Ce nom est réservé aux bots": "This name is kept for the bots",
    "Icône inconnue": "Unknown icon",
    "Trop de changements, réessaie dans quelques minutes": "Too many changes, try again in a few minutes",
  },
};

export default en;
